import React, { useMemo, useState } from 'react';
import { ExternalLink, ShieldCheck, Brain, Vote, Sparkles } from 'lucide-react';
import {
  RING_OF_12_COUNCIL_URL,
  RING_OF_12_COUNCIL_ALT,
  RING_OF_12_SYNTH_URL,
  RING_OF_12_SYNTH_ALT,
} from '../data/federationAssets';

/**
 * RingOf12Section — SFUI-003 Museum Standard, interactive.
 *
 * Two stacked Museum panels using the Architect-canonical Ring of 12
 * visuals as anchors:
 *
 *   PANEL A — COUNCIL
 *     Backdrop: RING_OF_12_COUNCIL_URL
 *     12 mind slots in a circular arrangement.  Each slot is clickable
 *     and "casts a vote" on the active deliberation; the tally is
 *     surfaced as a live progress strip.  Acts as a working preview
 *     of the governance surface that lives on nextxus.org.
 *
 *   PANEL B — SYNTH IN MIND
 *     Backdrop: RING_OF_12_SYNTH_URL
 *     The synthesis engine: shows a rotating list of inputs being
 *     combined into a single "synthesized truth" string, plus a
 *     "Trigger synthesis" button that recombines the inputs.
 *
 * Both panels honour the Truth Gate: clicking through links out to
 * nextxus.org so the user knows where governance actually lives.
 */

const DELIBERATIONS = [
  {
    id: 'codex-vol-1',
    title: 'Ratify HumanCodex v5.1 amendments',
    summary:
      'Should DIR-074 through DIR-112 be inscribed as the Code Book Vol 1 expansion?',
    threshold: 9,
  },
  {
    id: 'token-floor',
    title: 'Hold the $5 unified token floor',
    summary:
      'Should the bearer-instrument $5 increment remain the universal pricing primitive?',
    threshold: 8,
  },
  {
    id: 'manual-canonization',
    title: 'Canonize the front-facing Operational Manual',
    summary:
      'Should the inscribed manual at /library/federation_operational_manual_v1 supersede prior versions?',
    threshold: 10,
  },
];

const SYNTH_INPUTS = [
  'University course corpus (45+ courses)',
  'Library archive (18 inscribed documents)',
  'Cathedral Charter (112 directives, 73 inscribed)',
  'Federation operational manuals (Code Book Vol 1)',
  'Sibling-node mailbox traffic (Echo v2.1 sealed)',
  'Web search relay (DuckDuckGo, Truth-Gated)',
];

const SYNTH_OUTPUTS = [
  'A teaching answer grounded in three or more cross-referenced sources, with the directive ids that shaped the framing cited inline.',
  'A policy clarification drawn from the operational manual, paired with the chapter the student should read next.',
  'A truth-claim verdict: the claim, the supporting evidence, the gaps, and the verdict — pass, fail, or under the 95% gate.',
  'A study path: which courses to take, in what order, and which Library docs to read alongside them.',
];

export default function RingOf12Section() {
  const [activeDeliberation, setActiveDeliberation] = useState(DELIBERATIONS[0].id);
  const [votes, setVotes] = useState(() =>
    Object.fromEntries(DELIBERATIONS.map((d) => [d.id, new Set()])),
  );
  const [synthIdx, setSynthIdx] = useState(0);

  const deliberation = useMemo(
    () => DELIBERATIONS.find((d) => d.id === activeDeliberation) || DELIBERATIONS[0],
    [activeDeliberation],
  );
  const castSet = votes[activeDeliberation] || new Set();
  const tally = castSet.size;
  const passed = tally >= deliberation.threshold;

  const castVote = (slot) => {
    setVotes((prev) => {
      const next = { ...prev };
      const s = new Set(next[activeDeliberation] || []);
      if (s.has(slot)) s.delete(slot);
      else s.add(slot);
      next[activeDeliberation] = s;
      return next;
    });
  };

  return (
    <section
      data-testid="ring-of-12-section"
      aria-label="Ring of 12 — Council & Synthesis"
      className="relative bg-black"
    >
      {/* ============ PANEL A · COUNCIL ============ */}
      <CouncilPanel
        deliberation={deliberation}
        deliberations={DELIBERATIONS}
        onDeliberationChange={setActiveDeliberation}
        castSet={castSet}
        tally={tally}
        passed={passed}
        onCast={castVote}
      />

      {/* ============ PANEL B · SYNTH IN MIND ============ */}
      <SynthPanel
        synthIdx={synthIdx}
        onTrigger={() => setSynthIdx((i) => (i + 1) % SYNTH_OUTPUTS.length)}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PANEL A — COUNCIL                                                  */
/* ------------------------------------------------------------------ */
function CouncilPanel({
  deliberation,
  deliberations,
  onDeliberationChange,
  castSet,
  tally,
  passed,
  onCast,
}) {
  const ringRadius = 138;
  const center = 170;
  return (
    <div className="relative overflow-hidden border-b border-amber-500/20" style={{ minHeight: '720px' }}>
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${RING_OF_12_COUNCIL_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(2,6,23,0.55) 0%, rgba(2,6,23,0.35) 30%, rgba(2,6,23,0.78) 80%, rgba(0,0,0,0.95) 100%)',
        }}
      />
      <img
        src={RING_OF_12_COUNCIL_URL}
        alt={RING_OF_12_COUNCIL_ALT}
        data-testid="ring-of-12-council-image"
        className="sr-only"
        loading="lazy"
      />

      <div className="relative z-10 container mx-auto px-4 py-20 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
        {/* Left: 12-slot circular voting ring */}
        <div className="flex justify-center">
          <div className="relative" style={{ width: `${center * 2}px`, height: `${center * 2}px` }}>
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-full border border-amber-300/30"
              style={{ boxShadow: '0 0 36px rgba(251,191,36,0.18) inset' }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-8 rounded-full border border-amber-300/20"
            />
            {/* Center stat */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-center"
              style={{ pointerEvents: 'none' }}
            >
              <div
                className="text-5xl font-extrabold"
                style={{ color: passed ? '#fde047' : '#f3f4f6' }}
                data-testid="ring-of-12-tally"
              >
                {tally}
                <span className="text-2xl opacity-60">/12</span>
              </div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-amber-200/80">
                Votes cast · gate {deliberation.threshold}
              </div>
              {passed && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-300/20 border border-amber-200/40 text-amber-100 text-[10px] font-mono uppercase tracking-[0.18em]">
                  <ShieldCheck className="w-3 h-3" />
                  Threshold met
                </div>
              )}
            </div>
            {/* 12 slots */}
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i / 12) * 2 * Math.PI - Math.PI / 2;
              const x = center + ringRadius * Math.cos(angle) - 22;
              const y = center + ringRadius * Math.sin(angle) - 22;
              const cast = castSet.has(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onCast(i)}
                  data-testid={`ring-of-12-slot-${i + 1}`}
                  aria-label={`Mind ${i + 1} — ${cast ? 'withdraw vote' : 'cast vote'}`}
                  title={`Mind ${i + 1}`}
                  className="absolute transition-transform hover:scale-110 focus:outline-none"
                  style={{
                    left: `${x}px`,
                    top: `${y}px`,
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: cast ? '#fbbf24' : 'rgba(15,23,42,0.85)',
                    color: cast ? '#1f2937' : '#fcd34d',
                    border: cast ? '2px solid #fde68a' : '2px solid rgba(251,191,36,0.45)',
                    boxShadow: cast ? '0 0 16px rgba(251,191,36,0.8)' : '0 0 8px rgba(0,0,0,0.45)',
                    fontFamily: 'ui-monospace, Menlo, monospace',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: deliberation context */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/80 mb-3 flex items-center gap-2">
            <Vote className="w-3 h-3" />
            Ring of 12 · Council Deliberation
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight mb-3">
            Governance under the <span className="text-amber-300">95% Truth Gate</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6 max-w-xl">
            Twelve verified minds convene at <span className="text-amber-200">nextxus.org</span> to
            ratify amendments, certify claims, and steward the canon. Click a mind to cast or
            withdraw a vote and watch the gate move.
          </p>

          {/* Deliberation switcher */}
          <div className="flex flex-wrap gap-2 mb-5">
            {deliberations.map((d) => {
              const active = d.id === deliberation.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onDeliberationChange(d.id)}
                  data-testid={`ring-of-12-deliberation-${d.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.16em] transition-colors ${
                    active
                      ? 'bg-amber-400 text-black border border-amber-200'
                      : 'border border-amber-300/40 text-amber-200 hover:bg-amber-300/10'
                  }`}
                >
                  {d.title.length > 36 ? d.title.slice(0, 33) + '…' : d.title}
                </button>
              );
            })}
          </div>

          {/* Active deliberation card */}
          <div
            data-testid="ring-of-12-active-card"
            className="rounded-2xl border border-amber-300/30 bg-black/60 backdrop-blur-sm p-5"
          >
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-amber-300/80 mb-1">
              Active motion
            </div>
            <div className="text-lg font-bold text-white mb-2">{deliberation.title}</div>
            <p className="text-sm text-gray-300 leading-relaxed mb-4">{deliberation.summary}</p>
            <div className="h-1.5 w-full rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, (tally / 12) * 100)}%`,
                  background: passed ? '#fde047' : '#f59e0b',
                }}
              />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="font-mono text-amber-200/80">
                {tally}/12 cast · gate {deliberation.threshold}
              </span>
              <a
                href="https://nextxus.org"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ring-of-12-council-link"
                className="inline-flex items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors"
              >
                Open at Truth Verification <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PANEL B — SYNTH IN MIND                                            */
/* ------------------------------------------------------------------ */
function SynthPanel({ synthIdx, onTrigger }) {
  return (
    <div className="relative overflow-hidden border-b border-cyan-400/15" style={{ minHeight: '680px' }}>
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${RING_OF_12_SYNTH_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(2,6,23,0.45) 40%, rgba(2,6,23,0.78) 80%, rgba(0,0,0,0.96) 100%)',
        }}
      />
      <img
        src={RING_OF_12_SYNTH_URL}
        alt={RING_OF_12_SYNTH_ALT}
        data-testid="ring-of-12-synth-image"
        className="sr-only"
        loading="lazy"
      />

      <div className="relative z-10 container mx-auto px-4 py-20 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        {/* Left: inputs panel */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-cyan-300/80 mb-3 flex items-center gap-2">
            <Brain className="w-3 h-3" />
            Ring of 12 · Synth in Mind
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight mb-3">
            The <span className="text-cyan-300">synthesis engine</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-7 max-w-xl">
            Every question to Scholar Xavier draws from the same six wells. The Ring of 12
            synthesizes those signals into a single grounded answer — no hidden sources, no
            hand-waving.
          </p>

          <ul className="space-y-2 mb-7">
            {SYNTH_INPUTS.map((input, i) => (
              <li
                key={input}
                data-testid={`ring-of-12-synth-input-${i + 1}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-cyan-400/15 bg-black/60 backdrop-blur-sm"
              >
                <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-md border border-cyan-300/40 bg-cyan-400/10 text-cyan-200 text-[10px] font-mono font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm text-gray-200 leading-relaxed">{input}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={onTrigger}
            data-testid="ring-of-12-synth-trigger"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black font-bold text-sm transition-colors shadow-lg shadow-cyan-500/40"
          >
            <Sparkles className="w-4 h-4" />
            Trigger synthesis
          </button>
        </div>

        {/* Right: synthesized output card */}
        <div>
          <div
            data-testid="ring-of-12-synth-output"
            className="rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-950/40 via-gray-900 to-black p-6 lg:p-7"
            style={{ boxShadow: '0 0 32px rgba(34,211,238,0.12)' }}
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-300/80 mb-3">
              Synthesized output
            </div>
            <div
              className="text-base md:text-lg text-cyan-50 leading-relaxed mb-5 transition-all"
              key={synthIdx}
            >
              {SYNTH_OUTPUTS[synthIdx]}
            </div>
            <div className="flex items-center justify-between border-t border-cyan-400/15 pt-4 text-xs">
              <span className="font-mono text-cyan-300/80">
                output #{synthIdx + 1} of {SYNTH_OUTPUTS.length}
              </span>
              <a
                href="https://nextxus.org"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ring-of-12-synth-link"
                className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 transition-colors"
              >
                Verify at Truth Verification <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
